import React, { useEffect, useState, useRef } from 'react';
import { 
  Compass, 
  Lock, 
  Plus, 
  Trash, 
  Check, 
  X, 
  Globe, 
  CheckCircle, 
  Warning, 
  Info,
  Sliders,
  CaretRight,
  EnvelopeSimple,
  Activity,
  Gear,
  CircleNotch,
  ChatCircleText,
  TelegramLogo,
  FloppyDisk
} from 'phosphor-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';
import UpgradeModal from './UpgradeModal';
import { useAuth } from '../context/AuthContext';

// Exposição das variáveis permitidas de template na UI
const TEMPLATE_VARIABLES = [
  { name: "{asset}", desc: "Ativo (ex. BTC)" },
  { name: "{symbol}", desc: "Símbolo (ex. BTCUSD)" },
  { name: "{direction}", desc: "Direção (LONG/SHORT)" },
  { name: "{entry}", desc: "Preço de Entrada" },
  { name: "{stop}", desc: "Stop Loss" },
  { name: "{old_stop}", desc: "Stop Anterior" },
  { name: "{new_stop}", desc: "Novo Stop" },
  { name: "{target}", desc: "Target/Realização" },
  { name: "{quantity}", desc: "Tamanho de Mão" },
  { name: "{closed_quantity}", desc: "Qtd. Fechada Parcial" },
  { name: "{remaining_quantity}", desc: "Qtd. Restante" },
  { name: "{close_price}", desc: "Preço de Saída" },
  { name: "{realized_pnl}", desc: "P&L Realizado ($)" },
  { name: "{realized_pnl_percent}", desc: "P&L Realizado (%)" },
  { name: "{trade_url}", desc: "Link para o GENI (Interno)" },
  { name: "{positions_url}", desc: "Link de Compartilhamento Público" },
  { name: "{setup_result}", desc: "Setup + Win Rate + Profit Factor (TRADE_OPENED)" },
  { name: "{rr}", desc: "RR Planejado do Trade (ex. 2.50R)" }
];

export const IntegrationsSettings = ({ language, isPro: propIsPro, openGlobalUpgrade }) => {
  const { user } = useAuth();
  const isPortuguese = language === 'ptBR';
  const [isPro, setIsPro] = useState(propIsPro);
  const [destinations, setDestinations] = useState([]);
  const [eventRules, setEventRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upgrade Modal local
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Form State: Add/Edit Destination
  const [showDestForm, setShowDestForm] = useState(false);
  const [editingDestId, setEditingDestId] = useState(null);
  const [destName, setDestName] = useState("");
  const [destWebhook, setDestWebhook] = useState("");
  const [destRoleId, setDestRoleId] = useState("");
  const [destEnabled, setDestEnabled] = useState(true);
  const [isSavingDest, setIsSavingDest] = useState(false);
  const [testingDestId, setTestingDestId] = useState(null);

  // Selected event for rule configuration (collapsible)
  const [expandedEvent, setExpandedEvent] = useState("TRADE_OPENED");

  // Local rule editing states
  const [ruleEnabled, setRuleEnabled] = useState({});
  const [ruleDestination, setRuleDestination] = useState({});
  const [ruleMention, setRuleMention] = useState({});
  const [ruleTemplate, setRuleTemplate] = useState({});
  const [ruleScalpTemplate, setRuleScalpTemplate] = useState({});
  const [ruleSwingTemplate, setRuleSwingTemplate] = useState({});
  const [templateTab, setTemplateTab] = useState({}); // { [eventName]: 'default' | 'scalp' | 'swing' }
  const [savingRuleEvent, setSavingRuleEvent] = useState(null);
  const [isSavingAllRules, setIsSavingAllRules] = useState(false);

  // Trade routing states
  const [scalpDestId, setScalpDestId] = useState("");
  const [swingDestId, setSwingDestId] = useState("");
  const [defaultDestId, setDefaultDestId] = useState("");
  const [tradeRoutingEnabled, setTradeRoutingEnabled] = useState(false);
  const [isSavingRouting, setIsSavingRouting] = useState(false);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      // Fetch profile for subscription
      const profileRes = await api.get('/user/profile');
      const proState = profileRes.data.subscription_status === 'pro' || profileRes.data.is_demo === true;
      setIsPro(proState);

      // Fetch destinations and event rules
      const destsRes = await api.get('/integrations/discord/destinations');
      setDestinations(destsRes.data);

      const rulesRes = await api.get('/integrations/discord/event-rules');
      setEventRules(rulesRes.data);

      // Initialize local state for each event rule
      const ruleEnabledMap = {};
      const ruleDestMap = {};
      const ruleMentionMap = {};
      const ruleTemplateMap = {};

      rulesRes.data.forEach(r => {
        ruleEnabledMap[r.event_name] = r.enabled;
        ruleDestMap[r.event_name] = r.destination_id || "";
        ruleMentionMap[r.event_name] = r.mention_role || false;
        ruleTemplateMap[r.event_name] = r.message_template || "";
      });

      setRuleEnabled(ruleEnabledMap);
      setRuleDestination(ruleDestMap);
      setRuleMention(ruleMentionMap);
      setRuleTemplate(ruleTemplateMap);

      // Initialize local state for specific templates
      const ruleScalpTemplateMap = {};
      const ruleSwingTemplateMap = {};
      rulesRes.data.forEach(r => {
        ruleScalpTemplateMap[r.event_name] = r.scalp_message_template || "";
        ruleSwingTemplateMap[r.event_name] = r.swing_message_template || "";
      });
      setRuleScalpTemplate(ruleScalpTemplateMap);
      setRuleSwingTemplate(ruleSwingTemplateMap);

      // Fetch trade routing
      try {
        const routingRes = await api.get('/integrations/discord/routing');
        setTradeRoutingEnabled(routingRes.data.trade_routing_enabled || false);
        setScalpDestId(routingRes.data.scalp_daytrade_destination_id || "");
        setSwingDestId(routingRes.data.swing_trade_destination_id || "");
        setDefaultDestId(routingRes.data.default_destination_id || "");
      } catch (err) {
        console.error('Error fetching routing config:', err);
      }

      // Fetch logs
      const logsRes = await api.get('/integrations/logs');
      setLogs(logsRes.data);

    } catch (e) {
      console.error('Error fetching integrations data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, [propIsPro]);

  const handleOpenUpgrade = () => {
    if (openGlobalUpgrade) {
      openGlobalUpgrade();
    } else {
      setShowUpgrade(true);
    }
  };

  const handleCreateOrUpdateDest = async (e) => {
    e.preventDefault();
    if (!isPro) {
      handleOpenUpgrade();
      return;
    }

    if (!destName.trim()) {
      toast.error(isPortuguese ? "Preencha o nome do destino." : "Please fill in the destination name.");
      return;
    }

    // Se estiver editando e a URL for mascarada, ou contiver bullet, mantemos o mascaramento na chamada
    const isMaskedUrl = destWebhook.includes("••••");
    if (!isMaskedUrl && (!destWebhook.trim() || !destWebhook.startsWith("https://discord.com/api/webhooks/"))) {
      toast.error(isPortuguese ? "URL de webhook do Discord inválida." : "Invalid Discord Webhook URL.");
      return;
    }

    setIsSavingDest(true);
    try {
      const payload = {
        name: destName,
        webhook_url: destWebhook,
        role_id: destRoleId.trim() || null,
        enabled: destEnabled
      };

      if (editingDestId) {
        await api.put(`/integrations/discord/destinations/${editingDestId}`, payload);
        toast.success(isPortuguese ? "Destino atualizado com sucesso!" : "Destination updated successfully!");
      } else {
        await api.post('/integrations/discord/destinations', payload);
        toast.success(isPortuguese ? "Destino adicionado com sucesso!" : "Destination added successfully!");
      }

      // Reset Form
      setEditingDestId(null);
      setDestName("");
      setDestWebhook("");
      setDestRoleId("");
      setDestEnabled(true);
      setShowDestForm(false);
      
      await fetchIntegrations();
    } catch (err) {
      console.error('Error saving destination:', err);
      toast.error(err.response?.data?.detail || (isPortuguese ? "Erro ao salvar destino." : "Failed to save destination."));
    } finally {
      setIsSavingDest(false);
    }
  };

  const handleEditDest = (dest) => {
    setEditingDestId(dest.id);
    setDestName(dest.name);
    setDestWebhook(dest.webhook_url);
    setDestRoleId(dest.role_id || "");
    setDestEnabled(dest.enabled);
    setShowDestForm(true);
  };

  const handleDeleteDest = async (destId) => {
    if (!isPro) {
      handleOpenUpgrade();
      return;
    }
    const confirmDelete = window.confirm(
      isPortuguese 
        ? "Tem certeza de que deseja excluir este destino? Regras de evento utilizando este destino serão desabilitadas."
        : "Are you sure you want to delete this destination? Event rules using this destination will be disabled."
    );
    if (!confirmDelete) return;

    try {
      await api.delete(`/integrations/discord/destinations/${destId}`);
      toast.success(isPortuguese ? "Destino removido!" : "Destination deleted!");
      await fetchIntegrations();
    } catch (err) {
      toast.error(isPortuguese ? "Erro ao remover destino." : "Failed to delete destination.");
    }
  };

  const handleTestConnection = async (destId) => {
    if (!isPro) {
      handleOpenUpgrade();
      return;
    }
    setTestingDestId(destId);
    try {
      const response = await api.post(`/integrations/discord/destinations/${destId}/test`);
      if (response.data.success) {
        toast.success(isPortuguese ? "Mensagem de teste enviada com sucesso!" : "Test message sent successfully!");
      } else {
        toast.error(isPortuguese ? "Não foi possível enviar mensagem de teste. Verifique o webhook." : "Could not send test message. Check your webhook.");
      }
    } catch (err) {
      toast.error(isPortuguese ? "Erro de conexão ao testar webhook." : "Connection error testing webhook.");
    } finally {
      setTestingDestId(null);
    }
  };

  const handleSaveRule = async (eventName) => {
    if (!isPro) {
      handleOpenUpgrade();
      return;
    }
    setSavingRuleEvent(eventName);
    try {
      const payload = {
        enabled: ruleEnabled[eventName],
        destination_id: ruleDestination[eventName] || null,
        mention_role: ruleMention[eventName],
        message_template: ruleTemplate[eventName],
        scalp_message_template: ruleScalpTemplate[eventName] || "",
        swing_message_template: ruleSwingTemplate[eventName] || ""
      };
      
      // Validação: se habilitada, exige destino quando o roteamento de trade está desligado.
      // Se roteamento está ligado, não exige destino direto, mas exige que exista pelo menos um canal roteado.
      if (payload.enabled && !tradeRoutingEnabled && !payload.destination_id) {
        toast.error(isPortuguese ? "Selecione um destino Discord para habilitar a publicação." : "Select a Discord destination to enable publishing.");
        setSavingRuleEvent(null);
        return;
      }
      if (payload.enabled && tradeRoutingEnabled && !scalpDestId && !swingDestId && !defaultDestId) {
        toast.error(isPortuguese ? "Configure pelo menos um canal de destino no Trade Routing para habilitar a publicação." : "Configure at least one destination channel in Trade Routing to enable publishing.");
        setSavingRuleEvent(null);
        return;
      }

      await api.put(`/integrations/discord/event-rules/${eventName}`, payload);
      toast.success(isPortuguese ? "Regra de publicação salva!" : "Publish rule saved!");
      await fetchIntegrations();
    } catch (err) {
      toast.error(isPortuguese ? "Erro ao salvar regra." : "Failed to save rule.");
    } finally {
      setSavingRuleEvent(null);
    }
  };

  const handleInsertVariable = (eventName, variable) => {
    const currentTab = templateTab[eventName] || "default";
    if (currentTab === "scalp") {
      const currentText = ruleScalpTemplate[eventName] || "";
      const updated = currentText + " " + variable;
      setRuleScalpTemplate({
        ...ruleScalpTemplate,
        [eventName]: updated
      });
    } else if (currentTab === "swing") {
      const currentText = ruleSwingTemplate[eventName] || "";
      const updated = currentText + " " + variable;
      setRuleSwingTemplate({
        ...ruleSwingTemplate,
        [eventName]: updated
      });
    } else {
      const currentText = ruleTemplate[eventName] || "";
      const updated = currentText + " " + variable;
      setRuleTemplate({
        ...ruleTemplate,
        [eventName]: updated
      });
    }
  };

  const handleSaveAllRules = async () => {
    if (!isPro) {
      handleOpenUpgrade();
      return;
    }
    setIsSavingAllRules(true);
    try {
      const events = ["TRADE_OPENED", "PARTIAL_CLOSE", "STOP_UPDATED", "STOP_TRIGGERED", "TRADE_CLOSED"];
      
      // Validação rápida: se algum estiver enabled e não tiver destination (quando routing está desativado)
      for (const eventName of events) {
        if (ruleEnabled[eventName] && !tradeRoutingEnabled && !ruleDestination[eventName]) {
          toast.error(isPortuguese 
            ? `Selecione um destino para a regra: ${eventName}` 
            : `Select a destination for rule: ${eventName}`);
          setIsSavingAllRules(false);
          return;
        }
      }

      await Promise.all(events.map(async (eventName) => {
        const payload = {
          enabled: !!ruleEnabled[eventName],
          destination_id: ruleDestination[eventName] || null,
          mention_role: !!ruleMention[eventName],
          message_template: ruleTemplate[eventName] || "",
          scalp_message_template: ruleScalpTemplate[eventName] || "",
          swing_message_template: ruleSwingTemplate[eventName] || ""
        };
        await api.put(`/integrations/discord/event-rules/${eventName}`, payload);
      }));

      toast.success(isPortuguese ? "Todas as Regras de Eventos salvas com sucesso!" : "All Event Rules saved successfully!");
      await fetchIntegrations();
    } catch (err) {
      toast.error(isPortuguese ? "Erro ao salvar regras de eventos." : "Failed to save event rules.");
    } finally {
      setIsSavingAllRules(false);
    }
  };

  // Renderizador de Markdown de link discreto do Discord ([Texto](<URL>) ou [Texto](URL) -> span azul clicável)
  const renderPreviewText = (text) => {
    if (!text) return "";
    
    const regex = /\[([^\]]+)\]\(<?([^>)]+)>?\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      const matchIndex = match.index;
      if (matchIndex > lastIndex) {
        parts.push(text.substring(lastIndex, matchIndex));
      }
      
      const linkText = match[1];
      const linkUrl = match[2];
      
      parts.push(
        <span 
          key={matchIndex} 
          className="text-[#5865F2] md:text-[#00B0F4] hover:underline cursor-pointer font-semibold transition-all"
          title={linkUrl}
          onClick={() => {
            toast.info(isPortuguese ? `Link simulado do Discord para: ${linkUrl}` : `Simulated Discord link to: ${linkUrl}`);
          }}
        >
          {linkText}
        </span>
      );
      
      lastIndex = regex.lastIndex;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Gerador de preview simulado de Discord (Local)
  const getSimulatedPreview = (eventName, templateText) => {
    if (!templateText) return "";
    
    // Migração/Limpeza rápida local para o preview coincidir com o comportamento do backend
    let cleanedText = templateText;
    cleanedText = cleanedText.replace(/\{\{positions_url\}\}/g, "{positions_url}");
    cleanedText = cleanedText.replace(/\{positions_url\}\}/g, "{positions_url}");
    cleanedText = cleanedText.replace(/\{\{positions_url\}/g, "{positions_url}");
    
    // Normalizar links antigos de markdown no preview também
    cleanedText = cleanedText.replace(/\[📊\s*(?:VER TRADE NO GENI|VIEW TRADE ON GENI|ACOMPANHAR NO GENI)\]\(\{+positions_url\}+\)/gi, 
      `[📊 ${isPortuguese ? 'Ver trade no GENI' : 'View trade on GENI'}](<{positions_url}>)`);
    cleanedText = cleanedText.replace(/\[📊\s*(?:VER TRADE NO GENI|VIEW TRADE ON GENI|ACOMPANHAR NO GENI)\]\(<\{+positions_url\}+\>\)/gi, 
      `[📊 ${isPortuguese ? 'Ver trade no GENI' : 'View trade on GENI'}](<{positions_url}>)`);

    // Dados fictícios para simular a mensagem com a nova URL de produção fictícia
    const mockData = {
      "{asset}": "BTC",
      "{symbol}": "BTCUSD",
      "{direction}": "LONG",
      "{entry}": "112,500.00",
      "{stop}": "109,800.00",
      "{old_stop}": "108,000.00",
      "{new_stop}": "111,000.00",
      "{target}": "124,000.00",
      "{quantity}": "0.15 BTC",
      "{closed_quantity}": "50%",
      "{remaining_quantity}": "50%",
      "{close_price}": "122,100.00",
      "{realized_pnl}": "+$1,440.00",
      "{realized_pnl_percent}": "+4.2% (1.50R)",
      "{trade_url}": "https://genitradeapp.com/positions/demo-trader",
      "{positions_url}": "https://genitradeapp.com/positions/demo-trader",
      "{rr}": "2.50R"
    };

    let result = cleanedText;
    Object.keys(mockData).forEach(key => {
      // Escape special characters in key for RegExp
      const escapedKey = key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
      result = result.replace(new RegExp(escapedKey, 'g'), mockData[key]);
    });

    // Sanitização final de chaves redundantes ao redor de URLs
    result = result.replace(/\(\{+(https?:\/\/[^\}]+)\}+\)/g, "($1)");
    result = result.replace(/\(<\{+(https?:\/\/[^\}]+)\}+\>\)/g, "(<$1>)");

    const isMentionEnabled = ruleMention[eventName];
    const destId = ruleDestination[eventName];
    const destination = destinations.find(d => d.id === destId);
    if (isMentionEnabled && destination?.role_id) {
      result = `@${isPortuguese ? 'Cargo-Mention' : 'Role-Mention'}\n${result}`;
    }

    return result;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <CircleNotch size={32} className="animate-spin text-[#00E676]" />
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="integrations-settings-section">
      
      {/* Centralized Discord Card */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20">
            <ChatCircleText size={20} className="text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span>Discord Trade Publishing</span>
              {!isPro && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20 px-2 py-0.5 text-xs font-semibold">
                  <Lock size={11} weight="fill" /> PRO
                </span>
              )}
            </h2>
            <p className="text-white/50 text-sm">
              {isPortuguese 
                ? 'Publique automaticamente os eventos das suas operações em seus servidores Discord.'
                : 'Automatically publish your GENI trade events to your own Discord communities.'}
            </p>
          </div>
        </div>

        {/* destinations configuration */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5 sm:p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-white/5 pb-4">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white/70">
                {isPortuguese ? '1. Destinos Discord' : '1. Discord Destinations'}
              </h3>
              <p className="text-xs text-white/45 mt-0.5">
                {isPortuguese 
                  ? 'Configure um ou mais webhooks de canais do seu servidor.'
                  : 'Configure one or multiple webhooks pointing to your Discord channels.'}
              </p>
            </div>
            
            {!showDestForm && (
              <Button
                onClick={() => {
                  if (!isPro) {
                    handleOpenUpgrade();
                    return;
                  }
                  setEditingDestId(null);
                  setDestName("");
                  setDestWebhook("");
                  setDestRoleId("");
                  setDestEnabled(true);
                  setShowDestForm(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"
                data-testid="add-destination-button"
              >
                <Plus size={14} weight="bold" />
                {isPortuguese ? 'Adicionar Canal' : 'Add Destination'}
              </Button>
            )}
          </div>

          {/* Destination Add/Edit Form */}
          {showDestForm && (
            <form onSubmit={handleCreateOrUpdateDest} className="bg-black/25 border border-white/5 p-4 rounded-lg space-y-4">
              <div className="flex items-center justify-between border-b border-white/5 pb-2">
                <h4 className="text-xs font-black uppercase text-indigo-400">
                  {editingDestId 
                    ? (isPortuguese ? 'Editar Destino' : 'Edit Destination') 
                    : (isPortuguese ? 'Novo Destino Discord' : 'New Discord Destination')}
                </h4>
                <button
                  type="button"
                  onClick={() => setShowDestForm(false)}
                  className="text-white/40 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-white/55 font-bold uppercase tracking-wide">
                    {isPortuguese ? 'Nome do Canal (Interno)' : 'Channel Name (Internal)'}
                  </Label>
                  <Input
                    type="text"
                    value={destName}
                    onChange={(e) => setDestName(e.target.value)}
                    placeholder="ex. #sinais-bitcoin"
                    className="bg-zinc-950 border-white/10 text-xs focus:border-indigo-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-white/55 font-bold uppercase tracking-wide">
                    {isPortuguese ? 'URL do Webhook do Discord' : 'Discord Webhook URL'}
                  </Label>
                  <Input
                    type="text"
                    value={destWebhook}
                    onChange={(e) => setDestWebhook(e.target.value)}
                    placeholder="https://discord.com/api/webhooks/..."
                    className="bg-zinc-950 border-white/10 text-xs focus:border-indigo-500 font-mono"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-white/55 font-bold uppercase tracking-wide flex items-center gap-1">
                    <span>Role ID ({isPortuguese ? 'Mencionar Cargo' : 'Mention Role'})</span>
                    <span className="text-[10px] text-white/30 lowercase font-medium">({isPortuguese ? 'opcional' : 'optional'})</span>
                  </Label>
                  <Input
                    type="text"
                    value={destRoleId}
                    onChange={(e) => setDestRoleId(e.target.value)}
                    placeholder="ex. 1234567890123456"
                    className="bg-zinc-950 border-white/10 text-xs focus:border-indigo-500 font-mono"
                  />
                </div>

                <div className="flex items-center justify-between sm:pt-6 px-1">
                  <span className="text-xs text-white/55 font-bold uppercase tracking-wide">
                    {isPortuguese ? 'Habilitado' : 'Enabled'}
                  </span>
                  <Switch
                    checked={destEnabled}
                    onCheckedChange={setDestEnabled}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowDestForm(false)}
                  className="text-white/60 hover:bg-white/5 text-xs font-bold"
                >
                  {isPortuguese ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  disabled={isSavingDest}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold px-4 flex items-center gap-1.5"
                >
                  {isSavingDest ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />}
                  {isPortuguese ? 'Salvar Canal' : 'Save Channel'}
                </Button>
              </div>
            </form>
          )}

          {/* Destinations list */}
          <div className="space-y-3">
            {destinations.length === 0 ? (
              <div className="border border-dashed border-white/10 rounded-lg p-6 text-center text-white/40 text-xs">
                {isPortuguese 
                  ? 'Nenhum destino Discord configurado ainda.' 
                  : 'No Discord destinations configured yet.'}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {destinations.map((dest) => (
                  <div key={dest.id} className="border border-white/5 bg-black/15 p-4 rounded-xl flex flex-col justify-between space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-white leading-tight">{dest.name}</p>
                          <span className={`h-2 w-2 rounded-full ${dest.enabled ? 'bg-[#00E676]' : 'bg-white/20'}`} />
                        </div>
                        <p className="text-[10px] text-white/35 font-mono break-all mt-1">{dest.webhook_url}</p>
                        {dest.role_id && (
                          <p className="text-[10px] text-indigo-400 font-mono mt-1">Role: {dest.role_id}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => handleEditDest(dest)}
                          className="h-7 px-2 text-xs text-white/60 hover:text-white"
                        >
                          {isPortuguese ? 'Editar' : 'Edit'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => handleDeleteDest(dest.id)}
                          className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/5"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-white/5">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-white/30">
                        {dest.enabled ? (isPortuguese ? 'ATIVO' : 'ACTIVE') : (isPortuguese ? 'INATIVO' : 'INACTIVE')}
                      </span>
                      <Button
                        type="button"
                        disabled={testingDestId === dest.id}
                        onClick={() => handleTestConnection(dest.id)}
                        className="bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[11px] font-bold h-7 px-3 rounded-full flex items-center gap-1.5"
                      >
                        {testingDestId === dest.id ? <CircleNotch size={11} className="animate-spin" /> : "🧪"}
                        {isPortuguese ? 'Testar Canal' : 'Test Channel'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Trade Routing Section */}
          <div className="border-t border-white/5 pt-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white/70">
                  {isPortuguese ? '2. Roteamento de Operações (Trade Routing)' : '2. Trade Routing'}
                </h3>
                <p className="text-xs text-white/45 mt-0.5">
                  {isPortuguese 
                    ? 'Quando ativado, o GENI escolhe automaticamente o destino do Discord com base no tipo da operação.'
                    : 'When enabled, GENI automatically chooses the Discord destination based on the trade type.'}
                </p>
              </div>

              {/* Toggle de Roteamento de Trade */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white/60">
                  {tradeRoutingEnabled ? (isPortuguese ? 'ATIVADO' : 'ENABLED') : (isPortuguese ? 'DESATIVADO' : 'DISABLED')}
                </span>
                <Switch
                  checked={tradeRoutingEnabled}
                  onCheckedChange={async (val) => {
                    setTradeRoutingEnabled(val);
                    // Salvar o estado do toggle imediatamente em background para melhor UX
                    try {
                      await api.put('/integrations/discord/routing', {
                        trade_routing_enabled: val,
                        scalp_daytrade_destination_id: scalpDestId || null,
                        swing_trade_destination_id: swingDestId || null,
                        default_destination_id: defaultDestId || null
                      });
                      toast.success(isPortuguese ? "Status do roteamento atualizado!" : "Routing status updated!");
                      await fetchIntegrations();
                    } catch (err) {
                      toast.error(isPortuguese ? "Erro ao atualizar status do roteamento." : "Failed to update routing status.");
                    }
                  }}
                  disabled={!isPro}
                />
              </div>
            </div>

            <div className="bg-black/15 border border-white/5 p-4 sm:p-5 rounded-xl space-y-4">
              {!tradeRoutingEnabled ? (
                <div className="text-xs text-white/45 italic py-2">
                  {isPortuguese 
                    ? "Todos os eventos usam o destino configurado diretamente em cada regra."
                    : "All events use the Discord destination configured directly in each Event Rule."}
                </div>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-3">
                    {/* Scalp / Daytrade Routing */}
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-bold text-white/45 uppercase tracking-wider">
                        ⚡ SCALP / DAYTRADE
                      </label>
                      <select
                        value={scalpDestId}
                        onChange={(e) => setScalpDestId(e.target.value)}
                        disabled={!isPro}
                        className="w-full bg-zinc-950 border border-white/10 rounded-lg p-2 text-xs font-bold text-white focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="">{isPortuguese ? '-- Sem Filtro / Regra Geral --' : '-- No Routing / Event Rule Destination --'}</option>
                        {destinations.map(d => (
                          <option key={d.id} value={d.id}>{d.name} {d.enabled ? '' : `(${isPortuguese ? 'Inativo' : 'Inactive'})`}</option>
                        ))}
                      </select>
                    </div>

                    {/* Swing Trade Routing */}
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-bold text-white/45 uppercase tracking-wider">
                        📈 SWING TRADE
                      </label>
                      <select
                        value={swingDestId}
                        onChange={(e) => setSwingDestId(e.target.value)}
                        disabled={!isPro}
                        className="w-full bg-zinc-950 border border-white/10 rounded-lg p-2 text-xs font-bold text-white focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="">{isPortuguese ? '-- Sem Filtro / Regra Geral --' : '-- No Routing / Event Rule Destination --'}</option>
                        {destinations.map(d => (
                          <option key={d.id} value={d.id}>{d.name} {d.enabled ? '' : `(${isPortuguese ? 'Inativo' : 'Inactive'})`}</option>
                        ))}
                      </select>
                    </div>

                    {/* Default / Other Fallback Routing */}
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-bold text-white/45 uppercase tracking-wider">
                        🛡️ DEFAULT / OTHER
                      </label>
                      <select
                        value={defaultDestId}
                        onChange={(e) => setDefaultDestId(e.target.value)}
                        disabled={!isPro}
                        className="w-full bg-zinc-950 border border-white/10 rounded-lg p-2 text-xs font-bold text-white focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="">{isPortuguese ? '-- Nenhum (None) --' : '-- None --'}</option>
                        {destinations.map(d => (
                          <option key={d.id} value={d.id}>{d.name} {d.enabled ? '' : `(${isPortuguese ? 'Inativo' : 'Inactive'})`}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Botão de Salvar Roteamento */}
                  {isPro && (
                    <div className="flex justify-end pt-2 border-t border-white/5">
                      <Button
                        onClick={async () => {
                          setIsSavingRouting(true);
                          try {
                            await api.put('/integrations/discord/routing', {
                              trade_routing_enabled: tradeRoutingEnabled,
                              scalp_daytrade_destination_id: scalpDestId || null,
                              swing_trade_destination_id: swingDestId || null,
                              default_destination_id: defaultDestId || null
                            });
                            toast.success(isPortuguese ? "Configurações de roteamento salvas!" : "Routing configuration saved!");
                            await fetchIntegrations();
                          } catch (err) {
                            toast.error(isPortuguese ? "Erro ao salvar roteamento." : "Failed to save routing configuration.");
                          } finally {
                            setIsSavingRouting(false);
                          }
                        }}
                        disabled={isSavingRouting}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold px-4 flex items-center gap-1.5 rounded-lg h-8"
                      >
                        {isSavingRouting ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />}
                        {isPortuguese ? 'Salvar Roteamento' : 'Save Routing'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Event Rules Section */}
          <div className="border-t border-white/5 pt-6 space-y-5">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white/70">
                {isPortuguese ? '3. Regras de Eventos' : '3. Event Rules'}
              </h3>
              <p className="text-xs text-white/45 mt-0.5">
                {isPortuguese 
                  ? 'Mapeie cada tipo de evento das operações para um canal de destino Discord e edite o template da mensagem.'
                  : 'Map each trade event type to a Discord channel destination and customize the template.'}
              </p>
            </div>

            <div className="space-y-2.5">
              {[
                { name: "TRADE_OPENED", title: isPortuguese ? "Operação Iniciada (New Trade)" : "New Trade Opened" },
                { name: "PARTIAL_CLOSE", title: isPortuguese ? "Realização Parcial (Partial Close)" : "Partial Realization" },
                { name: "STOP_UPDATED", title: isPortuguese ? "Stop Loss Atualizado (Stop Updated)" : "Stop Loss Updated" },
                { name: "STOP_TRIGGERED", title: isPortuguese ? "Stop Atingido (Stop Triggered)" : "Stop Triggered" },
                { name: "TRADE_CLOSED", title: isPortuguese ? "Operação Encerrada (Position Closed)" : "Position Closed" }
              ].map((evt) => {
                const isExpanded = expandedEvent === evt.name;
                
                return (
                  <div key={evt.name} className="border border-white/5 bg-zinc-950/20 rounded-xl overflow-hidden">
                    {/* Collapsible Header */}
                    <button
                      type="button"
                      onClick={() => setExpandedEvent(isExpanded ? "" : evt.name)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`h-2 w-2 rounded-full ${ruleEnabled[evt.name] ? 'bg-indigo-400' : 'bg-white/10'}`} />
                        <div>
                          <p className="text-xs sm:text-sm font-bold text-white">{evt.title}</p>
                          <p className="text-[10px] text-white/35 font-mono mt-0.5">Event: {evt.name}</p>
                        </div>
                      </div>
                      <CaretRight size={14} className={`text-white/30 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>

                    {/* Expandable Body */}
                    {isExpanded && (
                      <div className="p-4 sm:p-5 border-t border-white/5 bg-black/15 space-y-4" data-testid={`rule-body-${evt.name}`}>
                        <div className="grid gap-4 sm:grid-cols-3">
                          {/* Enable/Disable Event */}
                          <div className="flex flex-col justify-between border border-white/5 p-3 rounded-lg bg-[#121212]/50">
                            <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">
                              {isPortuguese ? 'Publicar automaticamente' : 'Auto Publish'}
                            </span>
                            <div className="flex items-center justify-between mt-2.5">
                              <span className="text-xs font-bold text-white/70">
                                {ruleEnabled[evt.name] ? (isPortuguese ? 'HABILITADO' : 'ENABLED') : (isPortuguese ? 'DESABILITADO' : 'DISABLED')}
                              </span>
                              <Switch
                                checked={ruleEnabled[evt.name] || false}
                                onCheckedChange={(val) => setRuleEnabled({ ...ruleEnabled, [evt.name]: val })}
                              />
                            </div>
                          </div>

                          {/* Destination selection */}
                          <div className="flex flex-col justify-between border border-white/5 p-3 rounded-lg bg-[#121212]/50">
                            <label className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-1">
                              {isPortuguese ? 'Canal de Destino Discord' : 'Discord Destination'}
                            </label>
                            {tradeRoutingEnabled ? (
                              <div className="w-full bg-zinc-950/40 border border-dashed border-white/10 rounded p-2 text-[11px] text-white/60 font-semibold mt-1 flex flex-col justify-center space-y-1">
                                <span className="text-indigo-400 font-bold">
                                  {isPortuguese ? '🔗 Usando Roteamento por Tipo' : '🔗 Using Trade Routing'}
                                </span>
                                <div className="text-[10px] text-white/40 space-y-0.5 leading-tight font-mono">
                                  <div>Scalp → {destinations.find(d => d.id === scalpDestId)?.name || 'None'}</div>
                                  <div>Swing → {destinations.find(d => d.id === swingDestId)?.name || 'None'}</div>
                                  <div>Default → {destinations.find(d => d.id === defaultDestId)?.name || 'None'}</div>
                                </div>
                              </div>
                            ) : (
                              <select
                                value={ruleDestination[evt.name] || ""}
                                onChange={(e) => setRuleDestination({ ...ruleDestination, [evt.name]: e.target.value })}
                                className="w-full bg-zinc-950 border border-white/10 rounded p-1.5 text-xs font-bold text-white mt-1"
                              >
                                <option value="">{isPortuguese ? '-- Selecione o Canal --' : '-- Select Destination --'}</option>
                                {destinations.map(d => (
                                  <option key={d.id} value={d.id}>{d.name} {d.enabled ? '' : `(${isPortuguese ? 'Inativo' : 'Inactive'})`}</option>
                                ))}
                              </select>
                            )}
                          </div>

                          {/* Mention Role Switch */}
                          <div className="flex flex-col justify-between border border-white/5 p-3 rounded-lg bg-[#121212]/50">
                            <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">
                              {isPortuguese ? 'Mencionar Cargo' : 'Mention Role'}
                            </span>
                            <div className="flex items-center justify-between mt-2.5">
                              <span className="text-xs font-bold text-white/70">
                                {ruleMention[evt.name] ? (isPortuguese ? 'MARCAR' : 'MENTION') : (isPortuguese ? 'NÃO MARCAR' : 'DONT MENTION')}
                              </span>
                              <Switch
                                checked={ruleMention[evt.name] || false}
                                onCheckedChange={(val) => setRuleMention({ ...ruleMention, [evt.name]: val })}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Template Type Selector Tab */}
                        <div className="space-y-2">
                          <label className="block text-[10px] font-bold text-white/40 uppercase tracking-wider">
                            {isPortuguese ? 'Customizar Template por Tipo de Trade' : 'Customize Template by Trade Type'}
                          </label>
                          <div className="flex gap-2">
                            {[
                              { id: 'default', label: isPortuguese ? 'Padrão / Outros' : 'Default / Others' },
                              { id: 'scalp', label: isPortuguese ? '⚡ Scalp / Daytrade' : '⚡ Scalp / Daytrade' },
                              { id: 'swing', label: isPortuguese ? '📈 Swing Trade' : '📈 Swing Trade' }
                            ].map(tab => {
                              const activeTab = templateTab[evt.name] || 'default';
                              const isSelected = activeTab === tab.id;
                              return (
                                <button
                                  key={tab.id}
                                  type="button"
                                  onClick={() => setTemplateTab({ ...templateTab, [evt.name]: tab.id })}
                                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                                    isSelected 
                                      ? 'bg-indigo-600/10 border-indigo-500/35 text-indigo-300' 
                                      : 'bg-zinc-950 border-white/5 text-white/50 hover:text-white hover:bg-white/5'
                                  }`}
                                >
                                  {tab.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Message Template textarea */}
                        <div className="space-y-2">
                          <label className="block text-[10px] font-bold text-white/40 uppercase tracking-wider">
                            {isPortuguese ? 'Template da Mensagem' : 'Message Template'}
                          </label>
                          <textarea
                            value={
                              (templateTab[evt.name] || 'default') === 'scalp'
                                ? (ruleScalpTemplate[evt.name] || "")
                                : (templateTab[evt.name] || 'default') === 'swing'
                                ? (ruleSwingTemplate[evt.name] || "")
                                : (ruleTemplate[evt.name] || "")
                            }
                            onChange={(e) => {
                              const activeTab = templateTab[evt.name] || 'default';
                              if (activeTab === 'scalp') {
                                setRuleScalpTemplate({ ...ruleScalpTemplate, [evt.name]: e.target.value });
                              } else if (activeTab === 'swing') {
                                setRuleSwingTemplate({ ...ruleSwingTemplate, [evt.name]: e.target.value });
                              } else {
                                setRuleTemplate({ ...ruleTemplate, [evt.name]: e.target.value });
                              }
                            }}
                            rows={5}
                            className="w-full bg-zinc-950 border border-white/10 rounded-lg p-3 text-xs font-mono text-white focus:border-indigo-500 focus:outline-none"
                            placeholder="🚨 **NEW TRADE | {asset}** ..."
                          />
                        </div>

                        {/* Template variables helpers */}
                        <div className="space-y-2">
                          <p className="text-[9px] font-bold text-white/30 uppercase tracking-wider">
                            {isPortuguese ? 'VARIÁVEIS DE ATALHO (CLIQUE PARA ADICIONAR)' : 'VARIABLE SHORTCUTS (CLICK TO INSERT)'}
                          </p>
                          <div className="flex flex-wrap gap-1.5 pt-0.5">
                            {TEMPLATE_VARIABLES.map(v => (
                              <button
                                key={v.name}
                                type="button"
                                onClick={() => handleInsertVariable(evt.name, v.name)}
                                title={v.desc}
                                className="bg-white/5 hover:bg-white/10 border border-white/5 px-2 py-1 rounded text-[10px] font-mono text-indigo-300 font-bold transition-all"
                              >
                                {v.name}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Template Preview Panel */}
                        <div className="space-y-2 border-t border-white/5 pt-3.5">
                          <p className="text-[9px] font-bold text-[#00E676]/70 uppercase tracking-wider">
                            {isPortuguese ? 'PREVIEW SIMULADO DO DISCORD (LOCAL)' : 'SIMULATED DISCORD PREVIEW (LOCAL)'}
                          </p>
                          <div className="bg-[#2C2F33] text-[#DCDDDE] rounded-lg p-3.5 font-sans text-[13px] leading-relaxed whitespace-pre-wrap select-none border border-black/25">
                            {renderPreviewText(getSimulatedPreview(
                              evt.name,
                              (templateTab[evt.name] || 'default') === 'scalp'
                                ? (ruleScalpTemplate[evt.name] || "")
                                : (templateTab[evt.name] || 'default') === 'swing'
                                ? (ruleSwingTemplate[evt.name] || "")
                                : (ruleTemplate[evt.name] || "")
                            )) || (
                              <span className="italic text-white/30">
                                {isPortuguese ? 'Nenhum texto preenchido.' : 'No text entered.'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Save Trigger */}
                        <div className="flex justify-end pt-2 border-t border-white/5">
                          <Button
                            onClick={() => handleSaveRule(evt.name)}
                            disabled={savingRuleEvent === evt.name}
                            className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold px-4 flex items-center gap-1.5 rounded-lg"
                          >
                            {savingRuleEvent === evt.name ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />}
                            {isPortuguese ? 'Salvar Configuração' : 'Save Configuration'}
                          </Button>
                        </div>

                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Activity Table */}
          <div className="border-t border-white/5 pt-6 space-y-4">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white/70 flex items-center gap-2">
                <Activity size={16} className="text-indigo-400" />
                <span>{isPortuguese ? 'Atividade Recente de Automação' : 'Recent Integration Activity'}</span>
              </h3>
              <p className="text-xs text-white/45 mt-0.5">
                {isPortuguese 
                  ? 'Acompanhe os status dos últimos disparos externos enviados pelo GENI.'
                  : 'Track the status of the latest external publishes dispatched by GENI.'}
              </p>
            </div>

            <div className="bg-black/15 border border-white/5 rounded-lg overflow-hidden">
              {logs.length === 0 ? (
                <div className="p-5 text-center text-white/30 text-xs italic">
                  {isPortuguese ? 'Nenhuma publicação realizada recentemente.' : 'No publishes performed recently.'}
                </div>
              ) : (
                <div className="overflow-x-auto text-xs">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-white/5 border-b border-white/5 text-[10px] text-white/40 uppercase tracking-wide">
                        <th className="p-3 font-semibold">{isPortuguese ? 'Evento' : 'Event'}</th>
                        <th className="p-3 font-semibold">{isPortuguese ? 'Destino' : 'Destination'}</th>
                        <th className="p-3 font-semibold">Status</th>
                        <th className="p-3 font-semibold">{isPortuguese ? 'Data/Hora' : 'Published At'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-white/80">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-white/[0.01]">
                          <td className="p-3 font-mono text-[11px] font-bold text-indigo-300">{log.event_name}</td>
                          <td className="p-3 font-semibold">{log.destination_name}</td>
                          <td className="p-3 font-semibold">
                            {log.status === 'sent' ? (
                              <span className="inline-flex items-center gap-1 rounded bg-green-500/10 text-green-400 border border-green-500/20 px-1.5 py-0.5 font-bold uppercase text-[9px]">
                                {isPortuguese ? 'ENVIADO' : 'SENT'}
                              </span>
                            ) : log.status === 'skipped' ? (
                              <span className="inline-flex items-center gap-1 rounded bg-white/10 text-white/50 border border-white/10 px-1.5 py-0.5 font-bold uppercase text-[9px]" title={log.error}>
                                {isPortuguese ? 'IGNORADO' : 'SKIPPED'}
                              </span>
                            ) : log.status === 'failed' ? (
                              <span className="inline-flex items-center gap-1 rounded bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 font-bold uppercase text-[9px]" title={log.error}>
                                {isPortuguese ? 'FALHOU' : 'FAILED'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-1.5 py-0.5 font-bold uppercase text-[9px]">
                                {isPortuguese ? 'PENDENTE' : 'PENDING'}
                              </span>
                            )}
                          </td>
                          <td className="p-3 font-semibold text-white/50">
                            {log.published_at ? new Date(log.published_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* Telegram Card (COMING SOON) */}
      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center border border-sky-500/20">
            <TelegramLogo size={20} className="text-sky-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span>Telegram Publishing</span>
              <span className="inline-flex rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 px-2 py-0.5 text-[10px] font-bold uppercase">
                {isPortuguese ? 'Em Breve' : 'Coming Soon'}
              </span>
            </h2>
            <p className="text-white/50 text-sm">
              {isPortuguese 
                ? 'Publique automaticamente as atualizações das suas operações em canais e grupos do Telegram.'
                : 'Automatically publish your GENI trade updates to Telegram channels and groups.'}
            </p>
          </div>
        </div>
      </section>

      {/* Local Upgrade Modal if no global trigger is provided */}
      {!openGlobalUpgrade && (
        <UpgradeModal
          isOpen={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          userEmail={user?.email}
        />
      )}

    </div>
  );
};

export default IntegrationsSettings;
